import {
  Button,
  ButtonVariant,
  FormMessage,
  FormMessageVariant,
  toast,
} from '@aus-platform/design-system';
import { isNil } from 'lodash';
import React, { useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import {
  handleFormInputMessage,
  useChangeLoggedUserPasswordRequest,
} from 'shared/api';
import { CustomDate } from 'shared/utils';
import {
  ChangePasswordForm,
  type ChangePasswordFormRef,
  type ChangePasswordValues,
} from './change-password-form';

// Types.
type PasswordEnforcementModalProps = {
  onClose: VoidFunction;
  passwordExpiresAt: string | null;
};

// Enums.
enum PasswordEnforcementView {
  Required = 'REQUIRED',
  ChangePassword = 'CHANGE_PASSWORD',
}

// Constants.
export const PASSWORD_ENFORCEMENT_MODAL_KEY = 'isPasswordEnforcementModalShown';

export const PasswordEnforcementModal: React.FC<
  PasswordEnforcementModalProps
> = ({ onClose, passwordExpiresAt }) => {
  // States.
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [currentView, setCurrentView] = useState<PasswordEnforcementView>(
    PasswordEnforcementView.Required,
  );

  // Refs.
  const formRef = useRef<ChangePasswordFormRef>(null);

  // Hooks.
  const {
    mutate: sendChangePasswordRequest,
    isPending: changePasswordRequestIsLoading,
  } = useChangeLoggedUserPasswordRequest();

  // Variables.
  const customDate = new CustomDate(passwordExpiresAt);

  const hoursRemaining = customDate?.getRemainingHours();

  const minutesRemaining = customDate?.getRemainingMinutes();

  const remainingTimeLabel =
    !isNil(hoursRemaining) && hoursRemaining > 1
      ? `${hoursRemaining} hours`
      : `${minutesRemaining} ${minutesRemaining === 1 ? 'minute' : 'minutes'}`;

  // Handlers.
  const handleUpdatePassword = (
    values: ChangePasswordValues,
    helpers: {
      reset: VoidFunction;
      setFormError: React.Dispatch<React.SetStateAction<string>>;
      setFieldErrors: React.Dispatch<
        React.SetStateAction<Record<string, string>>
      >;
    },
  ) => {
    sendChangePasswordRequest(values, {
      onSuccess: () => {
        toast.success('Password changed successfully.');
        helpers.reset();
        onClose();
      },

      onError: (error) => {
        handleFormInputMessage(
          false,
          true,
          undefined,
          error,
          helpers.setFieldErrors,
          helpers.setFormError,
        );
      },
    });
  };

  const handleChangePassword = () => {
    setCurrentView(PasswordEnforcementView.ChangePassword);
  };

  const renderRequiredView = () => {
    return (
      <>
        <div className="password-enforcement-modal__message">
          Your password will expire in {remainingTimeLabel}. You can update it
          now, or change it later from
          <strong> Settings → Profile</strong>.
        </div>

        <FormMessage
          showIcon
          variant={FormMessageVariant.Warning}
          message="If your password is not changed before it expires, you will be automatically logged out of your account."
        />
      </>
    );
  };

  const renderChangePasswordView = () => {
    return (
      <ChangePasswordForm
        ref={formRef}
        onSubmit={handleUpdatePassword}
        setIsSubmitDisabled={setIsSubmitDisabled}
      />
    );
  };

  const renderModalBody = () => {
    switch (currentView) {
      case PasswordEnforcementView.Required:
        return renderRequiredView();

      case PasswordEnforcementView.ChangePassword:
        return renderChangePasswordView();

      default:
        return null;
    }
  };

  const renderFooter = () => {
    switch (currentView) {
      case PasswordEnforcementView.Required:
        return (
          <>
            <Button variant={ButtonVariant.Secondary} onClick={onClose}>
              Skip For Now
            </Button>

            <Button onClick={handleChangePassword}> Change Password </Button>
          </>
        );

      case PasswordEnforcementView.ChangePassword:
        return (
          <Button
            disabled={isSubmitDisabled}
            isLoading={changePasswordRequestIsLoading}
            onClick={() => formRef.current?.submit()}
          >
            Change Password
          </Button>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      className="password-enforcement-modal"
      show
      centered
      backdrop="static"
      keyboard={false}
      onHide={onClose}
    >
      <Modal.Header closeButton>
        {currentView === PasswordEnforcementView.Required
          ? 'Password Change Required'
          : 'Change Password'}
      </Modal.Header>

      <Modal.Body>{renderModalBody()}</Modal.Body>

      <Modal.Footer>{renderFooter()}</Modal.Footer>
    </Modal>
  );
};
